import { useNavigate } from "react-router-dom";
import { OtpInputField } from "../../components";
import { useTranslation } from "react-i18next";
import Button from "../../components/Button";
import { useApi } from "../../hooks";
import { useAuthStore } from "../../stores";
import Text from "../../components/Text";
import { SVGs } from "../../assets";
import * as React from "react";
import { useSearchParams } from "../../hooks/searchParams";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

function ResendCountdown({ onResend = async () => {} }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = React.useState(RESEND_SECONDS);

  React.useEffect(() => {
    if (remaining <= 0) return;
    const id = window.setInterval(() => {
      setRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [remaining]);

  if (!remaining)
    return (
      <Button
        variant="text"
        color="secondary"
        children={t("emailVerification.resendCode")}
        onClick={async () => {
          await onResend();
          setRemaining(RESEND_SECONDS);
        }}
      />
    );
  return (
    <Text className="text-text-disabled">
      {t("emailVerification.resendIn", { seconds: remaining })}
    </Text>
  );
}

export default function EmailVerification() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchParams, setSearchParams } = useSearchParams({
    email: "",
    session: "",
    user_id: "",
  });
  const verifyEmail = useAuthStore((s) => s.verifyEmail);
  const login = useAuthStore((s) => s.login);
  const { fetch } = useApi();
  const [otp, setOtp] = React.useState("");

  React.useEffect(() => {
    if (!searchParams.email || !searchParams.session || !searchParams.user_id) {
      navigate("/", { replace: true });
    }
  }, [searchParams, navigate]);

  const resendCode = async () => {
    try {
      setOtp("");
      const res = await fetch(() => login(searchParams.email), {
        successMsg: () =>
          t("emailVerification.resendCodeSuccess", {
            email: searchParams.email,
          }),
      });
      if (!res) return;
      setSearchParams(res);
    } catch {}
  };

  return (
    <div className="flex items-center justify-center pt-[100px] px-4 py-10">
      <div className="w-full max-w-[540px] flex flex-col items-center">
        <img src={SVGs.logo} alt="Health 4 Travel" className="h-[40] w-auto" />
        <Text level={1} className="mt-[24px]">
          {t("emailVerification.title")}
        </Text>
        <Text className="mt-[8px] text-center">
          {t("emailVerification.instructionsPrefix")}{" "}
          <Text as="span" className="font-bold">
            {searchParams.email}
          </Text>
          . {t("emailVerification.instructionsSuffix")}
        </Text>
        <form onSubmit={(e) => e.preventDefault()}>
          <OtpInputField
            count={OTP_LENGTH}
            value={otp}
            onChange={(value) => setOtp(value)}
            className="mt-[24px]"
          />
          <div className="mt-[8px] self-start">
            <ResendCountdown onResend={resendCode} />
          </div>
          <div className="mt-[24px] w-full flex gap-[12px]">
            <Button
              className="w-full"
              color="secondary"
              onClick={() => navigate("/")}
              children={t("emailVerification.editEmail")}
            />
            <Button
              type="submit"
              className="w-full"
              children={t("emailVerification.verifyCta")}
              disabled={!searchParams.email || otp.length < OTP_LENGTH}
              onClick={async () => {
                try {
                  await fetch(() =>
                    verifyEmail(otp, {
                      session: searchParams.session,
                      user_id: searchParams.user_id,
                    }),
                  );
                  navigate("/");
                } catch {}
              }}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
